import { t } from "@lingui/macro";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";

type Props = {
  score: number;
  summary: string;
  ctaSlot?: ReactNode;
};

const getScoreColor = (score: number) => {
  if (score >= 70) return { stroke: "stroke-emerald-500", text: "text-success-strong" };
  if (score >= 50) return { stroke: "stroke-amber-500", text: "text-warning-strong" };
  return { stroke: "stroke-rose-500", text: "text-error" };
};

const getScoreLabel = (score: number) => {
  if (score >= 70) return t`Strong Profile`;
  if (score >= 50) return t`Needs Work`;
  return t`Significant Gaps`;
};

export const ScorePreview = ({ score, summary, ctaSlot }: Props) => {
  const clampedScore = Math.max(0, Math.min(100, score));
  const { stroke, text } = getScoreColor(clampedScore);

  // SVG ring parameters
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clampedScore / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-6 py-4">
      {/* Score ring */}
      <div className="relative flex items-center justify-center">
        <svg width="140" height="140" className="-rotate-90">
          {/* Track */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            strokeWidth="10"
            className="stroke-muted"
          />
          {/* Progress */}
          <circle
            cx="70"
            cy="70"
            r={radius}
            fill="none"
            strokeWidth="10"
            strokeLinecap="round"
            className={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={cn("font-mono text-4xl font-bold tabular-nums sm:text-6xl", text)}>
            {clampedScore}
          </span>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
      </div>

      {/* Score label */}
      <span className={cn("text-xs font-medium", text)}>{getScoreLabel(clampedScore)}</span>

      {/* Summary */}
      <p className="max-w-lg text-center text-sm leading-relaxed text-muted-foreground">
        {summary}
      </p>

      {/* CTA slot */}
      {ctaSlot}
    </div>
  );
};
